import React, { useEffect, useRef } from "react";
import "../../../css/ApplicantPreviewPrint.css";
import ERPUtils from "../../../helper/Utils";
import { Avatar } from "@mui/material";

export default function ApplicationPreviewPrint(props) {
  const calculateAgeHandler = props.calculateAge;
  const getDob = props.getDob;
  const data = props.previewData;
  const contentRef = useRef(null);
  useEffect(() => {
    // Function to calculate content height and apply page breaks
    function addPageBreaks() {
      const contentElement = contentRef.current;
      const pageHeight = 800; // Adjust this value based on your page height

      if (contentElement) {
        const contentHeight = contentElement.clientHeight;

        if (contentHeight > pageHeight) {
          // Content is taller than the page, prevent breaking inside
          contentElement.style.pageBreakInside = "avoid";
        } else {
          contentElement.style.pageBreakInside = "auto";
        }
      }
    }
    // Call the function when the component is mounted
    addPageBreaks();

    // Recalculate page breaks when the window is resized
    window.addEventListener("resize", addPageBreaks);

    return () => {
      // Remove the event listener when the component is unmounted
      window.removeEventListener("resize", addPageBreaks);
    };
  }, [data]);

  const groupedExperiences = {};
  // Function to calculate total experience in years and months
  const calculateTotalExperience = (experiences) => {
    let totalYears = 0;
    let totalMonths = 0;

    experiences.forEach((experience) => {
      totalYears += parseInt(experience.years, 10) || 0;
      totalMonths += parseInt(experience.months, 10) || 0;
    });

    totalYears += Math.floor(totalMonths / 12);
    totalMonths %= 12;

    return { totalYears, totalMonths };
  };

  // Add currentExperience to the groupedExperiences
  if (!ERPUtils.isNullorWhiteSpace(data?.professionalExperienceDTO?.currentExperience)) {
    const { workExperienceTypeId } = data.professionalExperienceDTO.currentExperience;
    if (!groupedExperiences[workExperienceTypeId]) {
      groupedExperiences[workExperienceTypeId] = [];
    }
    groupedExperiences[workExperienceTypeId].push(data?.professionalExperienceDTO?.currentExperience);
  }
  data.professionalExperienceDTO?.professionalExperienceList?.forEach((el) => {
    const { workExperienceTypeId } = el;

    if (!groupedExperiences[workExperienceTypeId]) {
      groupedExperiences[workExperienceTypeId] = [];
    }
    groupedExperiences[workExperienceTypeId].push(el);
  });

  const professionalExperience = Object.keys(groupedExperiences).map((workExperienceTypeId) => {
    // Calculate total experience dynamically for each workExperienceTypeId
    const experiences = groupedExperiences[workExperienceTypeId];
    const totalWorkExp = calculateTotalExperience(experiences);

    return (
      <div key={workExperienceTypeId}>
        <table className="table print-table">
          <thead>
            <tr>
              <th colSpan={2}>{workExperienceTypeId}</th>
              <th colSpan={4}>
                Total {workExperienceTypeId} experience: {totalWorkExp.totalYears} {totalWorkExp.totalYears > 1 ? "Years" : "Year"} {totalWorkExp.totalMonths}{" "}
                {totalWorkExp.totalMonths > 1 ? "Months" : "Month"}
              </th>
            </tr>
            <tr>
              <th style={{ width: "200px" }}>Institution</th>
              <th style={{ width: "150px" }}>Functional Area</th>
              <th style={{ width: "150px" }}>Designation</th>
              <th style={{ width: "150px" }}>
                <tr>From Date</tr>
                <tr>To Date</tr>
              </th>
              <th style={{ width: "150px" }}>
                <tr>Year(s)</tr>
                <tr>Month(s)</tr>
              </th>
              <th style={{ width: "100px" }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {groupedExperiences[workExperienceTypeId].map((experience) => (
              <tr key={experience?.empApplnWorkExperienceId}>
                <td>{experience?.institution || ""}</td>
                <td>{experience.functionalAreaOthers || experience?.functionalAreaId || ""}</td>
                <td>{experience?.designation || ""}</td>
                <td>
                  <tr>{ERPUtils.convertLocalDateToDD_MMM_YYYY_String(experience?.fromDate)} </tr>
                  <tr>{ERPUtils.convertLocalDateToDD_MMM_YYYY_String(experience?.toDate)}</tr>
                </td>
                <td>
                  {experience.years >= 1 ? (
                    ERPUtils.isUndefinedOrNull(experience.years) === false ? (
                      <tr>
                        {experience?.years} {experience.years > 1 ? "Years" : "Year"}{" "}
                      </tr>
                    ) : null
                  ) : null}
                  {experience?.months >= 1 ? (
                    ERPUtils.isUndefinedOrNull(experience?.months) === false ? (
                      <tr>
                        {experience?.months} {experience?.months > 1 ? "Months" : "Month"}
                      </tr>
                    ) : null
                  ) : null}
                </td>
                <td>{experience?.employmentType ? experience.employmentType.charAt(0).toUpperCase() + experience.employmentType.slice(1) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  });

  const calculateTotalExperiences = (experience) => {
    let totalYears = 0;
    let totalMonths = 0;

    if (experience.years) {
      totalYears += parseInt(experience.years);
    }
    if (experience.months) {
      totalMonths += parseInt(experience.months);
    }

    // Convert excess months to years
    totalYears += Math.floor(totalMonths / 12);
    totalMonths = totalMonths % 12;

    return { totalYears, totalMonths };
  };

  const calculateExperience = (experiences, employmentType) => {
    let totalYears = 0;
    let totalMonths = 0;

    for (const experience of experiences) {
      // Check if the experience matches the specified employment type
      if (experience.employmentType.toLowerCase() === employmentType.toLowerCase()) {
        const { totalYears: expYears, totalMonths: expMonths } = calculateTotalExperiences(experience);
        totalYears += expYears;
        totalMonths += expMonths;
      }
    }

    // Convert excess months to years
    totalYears += Math.floor(totalMonths / 12);
    totalMonths = totalMonths % 12;

    return { totalYears, totalMonths };
  };

  const profileSummaryTable = (
    <div>
      <table className="table print-table mt-2">
        <thead>
          <tr>
            <th>Work Experience Type</th>
            <th>Full Time Experience</th>
            <th>Part Time Experience</th>
            {/* <th>
              Total Experience
            </th> */}
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedExperiences).map((workExperienceTypeId) => {
            const fullTimeExperience = calculateExperience(groupedExperiences[workExperienceTypeId], "full time" || "fulltime");
            const partTimeExperience = calculateExperience(groupedExperiences[workExperienceTypeId], "part time" || "parttime");
            return (
              <tr>
                <td>{workExperienceTypeId}</td>
                <td>
                  {!ERPUtils.isNullorWhiteSpace(fullTimeExperience.totalYears) && fullTimeExperience.totalYears !== 0 && (
                    <>
                      {fullTimeExperience.totalYears} {fullTimeExperience.totalYears > 1 ? "Years" : "Year"}{" "}
                    </>
                  )}
                  {!ERPUtils.isNullorWhiteSpace(fullTimeExperience.totalMonths) && fullTimeExperience.totalMonths !== 0 && (
                    <>
                      {ERPUtils.isNullorWhiteSpace(fullTimeExperience.totalYears) || fullTimeExperience.totalYears === 0 ? "" : "&"}
                      {fullTimeExperience.totalMonths} {fullTimeExperience.totalMonths > 1 ? "Months" : "Month"}
                    </>
                  )}
                </td>
                <td>
                  {!ERPUtils.isNullorWhiteSpace(partTimeExperience.totalYears) && partTimeExperience.totalYears !== 0 && (
                    <>
                      {partTimeExperience.totalYears} {partTimeExperience.totalYears > 1 ? "Years" : "Year"}
                    </>
                  )}{" "}
                  {!ERPUtils.isNullorWhiteSpace(partTimeExperience.totalMonths) && partTimeExperience.totalMonths !== 0 && (
                    <>
                      {ERPUtils.isNullorWhiteSpace(partTimeExperience.totalYears) || partTimeExperience.totalYears === 0 ? "" : "&"}
                      {partTimeExperience.totalMonths} {partTimeExperience.totalMonths > 1 ? "Months" : "Month"}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          <tr>
            <td>
              <h6>
                <b>Total Experience</b>
              </h6>
            </td>
            <td>
              <h6>
                <b>
                  {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.totalPartTimePreviousExperienceYears) === false ||
                    ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.totalPreviousExperienceMonths) === false ||
                    ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.totalPartTimePreviousExperienceMonths) === false ||
                    ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.totalPreviousExperienceYears) === false ? (
                    <span>
                      {" "}
                      {!ERPUtils.isNullorWhiteSpace(data?.professionalExperienceDTO?.totalPreviousExperienceYears)
                        ? `${data?.professionalExperienceDTO?.totalPreviousExperienceYears} years `
                        : ""}{" "}
                      {!ERPUtils.isNullorWhiteSpace(data?.professionalExperienceDTO?.totalPreviousExperienceMonths)
                        ? `& ${data?.professionalExperienceDTO?.totalPreviousExperienceMonths} months`
                        : ""}
                    </span>
                  ) : (
                    "NA"
                  )}
                </b>
              </h6>
            </td>
            <td>
              {" "}
              <h6>
                <b>
                  {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.totalPartTimePreviousExperienceYears) === false ||
                    ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.totalPartTimePreviousExperienceMonths) === false ? (
                    <span>
                      {!ERPUtils.isNullorWhiteSpace(data.professionalExperienceDTO?.totalPartTimePreviousExperienceYears)
                        ? `${data.professionalExperienceDTO?.totalPartTimePreviousExperienceYears} years `
                        : ""}
                      {!ERPUtils.isNullorWhiteSpace(data.professionalExperienceDTO?.totalPartTimePreviousExperienceMonths)
                        ? `& ${data.professionalExperienceDTO?.totalPartTimePreviousExperienceMonths} months`
                        : ""}
                    </span>
                  ) : (
                    "NA"
                  )}
                </b>
              </h6>
            </td>
          </tr>
          <tr>
            <td> Salary Expected/Month</td>
            <td colSpan={2}>
              <label>
                {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.expectedSalary) === false ? data.professionalExperienceDTO?.expectedSalary : 0}
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const contactDetailsTable = (
    <div>
      <table className="table print-table mt-2">
        <tbody>
          <tr>
            <td className="pt-0 pb-0" style={{ width: "20%" }}>
              <span className="fa-solid fa-phone mr-1" />
              Mobile Number
            </td>
            <td className="pt-0 pb-0">
              <label>
                {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO) === false
                  ? `${data.empApplnPersonalDataDTO?.mobileNo || ""}${data.empApplnPersonalDataDTO?.alternateNo ? `, ${data.empApplnPersonalDataDTO?.alternateNo}` : ""
                  }`
                  : "-"}
              </label>
            </td>
          </tr>
          <tr>
            <td className="pt-0 pb-0" style={{ width: "20%" }}>
              <span className="fa fa-envelope SpanIcon mr-1" />
              Email ID
            </td>
            <td className="pt-0 pb-0">
              <label>
                {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO) === false
                  ? ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.emailId) === false
                    ? data.empApplnPersonalDataDTO?.emailId
                    : "-"
                  : "-"}
              </label>
            </td>
          </tr>
          <tr>
            <td className="pt-0 pb-0" style={{ width: "20%" }}>
              Current Address
            </td>
            <td className="pt-0 pb-0">
              <label>
                {`${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.currentAddressLine1) === false ? data.addressDetailDTO?.currentAddressLine1 : ""}, ${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.currentAddressLine2) === false ? data.addressDetailDTO?.currentAddressLine2 : ""
                  },`}{" "}
                {`${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.currentCityId) === false ? data.addressDetailDTO?.currentCityId : ""}, ${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.currentStateId) === false ? data.addressDetailDTO?.currentStateId : ""
                  },`}{" "}
                {ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.currentPincode) === false ? data.addressDetailDTO?.currentPincode : ""}
              </label>
            </td>
          </tr>
          <tr>
            <td className="pt-0 pb-0" style={{ width: "20%" }}>
              Permanent Address
            </td>
            <td className="pt-0 pb-0 ">
              <label>
                <div style={{ display: "inline-flex" }}>
                  {`${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.permanentAddressLine1) === false ? data.addressDetailDTO?.permanentAddressLine1 : ""}, ${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.permanentAddressLine2) === false ? data.addressDetailDTO?.permanentAddressLine2 : ""
                    },`}{" "}
                  {`${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.permanentCityId) === false ? data.addressDetailDTO?.permanentCityId : ""}, ${ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.permanentStateId) === false ? data.addressDetailDTO?.permanentStateId : ""
                    },`}{" "}
                  {ERPUtils.isUndefinedOrNull(data.addressDetailDTO?.permanentPincode) === false ? data.addressDetailDTO?.permanentPincode : ""}
                </div>
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const educationDetailsTable = (
    <div>
      <table className="table print-table mt-2">
        <thead>
          <tr>
            <th style={{ width: "150px" }}>Qualification & Subject</th>
            <th style={{ width: "200px" }}>Board/University</th>
            <th style={{ width: "200px" }}>Institution</th>
            <th style={{ width: "100px" }}>State & Country</th>
            <th style={{ width: "80px" }}>Marks %</th>
            <th style={{ width: "80px" }}>Year of Completion</th>
          </tr>
        </thead>
        <tbody>
          {ERPUtils.isUndefinedOrNull(data.educationalDetailDTO?.qualificationLevelsList) === false
            ? data.educationalDetailDTO?.qualificationLevelsList
              .sort((a, b) => parseInt(b.yearOfCompletion, 10) - parseInt(a.yearOfCompletion, 10))
              .map((el) => (
                <tr>
                  <td>
                    {ERPUtils.isNullorWhiteSpace(el?.qualificationLevelId) === false ? el?.qualificationLevelId : ""}{" "}
                    {ERPUtils.isNullorWhiteSpace(el?.course) === false ? "(" : ""}
                    {ERPUtils.isNullorWhiteSpace(el?.course) === false ? el?.course : ""}
                    {ERPUtils.isNullorWhiteSpace(el?.course) === false ? ")" : ""}
                    <br />
                    {ERPUtils.isNullorWhiteSpace(el?.specialization) === false ? el?.specialization : ""}
                  </td>
                  <td>{ERPUtils.isNullorWhiteSpace(el?.boardOrUniversity) === false ? el?.boardOrUniversity : ""}</td>
                  <td>{ERPUtils.isNullorWhiteSpace(el?.institute) === false ? el?.institute : ""}</td>
                  <td>
                    {ERPUtils.isNullorWhiteSpace(el?.stateName) === false ? el?.stateName : el?.stateOther},{" "}
                    {ERPUtils.isNullorWhiteSpace(el?.countryName) === false ? el?.countryName : ""}
                  </td>
                  <td>{ERPUtils.isNullorWhiteSpace(el?.gradeOrPercentage) === false ? el?.gradeOrPercentage : ""}</td>
                  <td>{ERPUtils.isNullorWhiteSpace(el?.yearOfCompletion) === false ? el?.yearOfCompletion : ""}</td>
                </tr>
              ))
            : null}
        </tbody>
      </table>
    </div>
  );

  const eligibilityTestTable = (
    <div>
      <table className="table print-table mt-2">
        <thead>
          <tr>
            <th>Exam Name</th>
            <th>Year</th>
          </tr>
        </thead>
        <tbody>
          {ERPUtils.isUndefinedOrNull(data.educationalDetailDTO?.eligibilityTestList) === false
            ? data.educationalDetailDTO?.eligibilityTestList.map((el) => (
              <tr>
                <td>{ERPUtils.isUndefinedOrNull(el?.eligibilityTestId) === false ? el?.eligibilityTestId : ""}</td>
                <td>{ERPUtils.isUndefinedOrNull(el.testYear) === false ? el?.testYear : ""}</td>
              </tr>
            ))
            : null}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-3 main-div-border-style">
      <div className="profile-details-heading">
        <div className="row">
          <div className="col-6">
            <div className="row ml-1">
              <div className="col-3">
                {data?.empApplnPersonalDataDTO?.profilePhotoUrl ? (
                  <span className="">
                    <img src={data?.empApplnPersonalDataDTO?.profilePhotoUrl} alt="profile-icons" height="90px" width="80px" style={{ borderRadius: "10%" }} />
                  </span>
                ) : (
                  <Avatar
                    variant="square"
                    sx={{
                      width: "80px",
                      marginRight: "5px",
                      height: "90px",
                    }}
                  >
                    {data.applicantName?.split("")[0]}
                  </Avatar>
                )}
              </div>
              <div className="col">
                {data.applicantName}
                <label className="">
                  {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO) === false ? (
                    <b>
                      {!ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.applicantName) && !ERPUtils.isUndefinedOrNull(data?.applicationNo)
                        ? `${data.empApplnPersonalDataDTO?.applicantName} ${`(${data?.applicationNo})`}`
                        : ""}
                    </b>
                  ) : null}
                  <br />
                  {!ERPUtils.isUndefinedOrNull(data.jobDetailDTO)
                    ? `Applying for the Post of ${!ERPUtils.isUndefinedOrNull(data?.empAppliedEmployeCategory?.label) ? data?.empAppliedEmployeCategory?.label : ""}
                ${!ERPUtils.isUndefinedOrNull(data.jobDetailDTO?.subjectCategoryIds) ? data.jobDetailDTO?.subjectCategoryIds.map((el) => `/${el}`) : ""}
                    ${!ERPUtils.isUndefinedOrNull(data.jobDetailDTO?.specializationIds) ? data.jobDetailDTO?.specializationIds.map((el) => `/${el}`) : ""} `
                    : null}
                  <br />
                  {ERPUtils.isUndefinedOrNull(data.jobDetailDTO) === false ? (
                    <b>
                      {!ERPUtils.isUndefinedOrNull(data.jobDetailDTO?.empApplnCampusPrefDTOList) ? data.jobDetailDTO?.empApplnCampusPrefDTOList.map((el) => `${el}, `) : ""}
                    </b>
                  ) : null}
                </label>
              </div>
            </div>
          </div>
          <div className="col-6">
            {ERPUtils.isUndefinedOrNull(data.empApplnSubjectCategory) === false || ERPUtils.isUndefinedOrNull(data?.campusPreference) === false ? (
              <>
                {ERPUtils.isNullorWhiteSpace(data.jobCategoryDTO) === false ? (
                  <div className="row">
                    <label className="col-5">
                      <span>Job Category</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data.jobCategoryDTO?.label) === false ? data.jobCategoryDTO?.label.split(",").join(", ") : ""}
                    </label>
                  </div>
                ) : (
                  ""
                )}
                {ERPUtils.isUndefinedOrNull(data?.empApplnSubjectCategorySpecialization) === false ? (
                  <div className="row">
                    <label className="col-5">
                      <span>Specialization</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data?.empApplnSubjectCategorySpecialization) === false
                        ? data?.empApplnSubjectCategorySpecialization.split(",").join(", ")
                        : ""}
                    </label>
                  </div>
                ) : (
                  ""
                )}
                {ERPUtils.isUndefinedOrNull(data?.campusPreference) === false && data?.campusPreference !== "null" ? (
                  <div className="row">
                    <label className="col-5">
                      <span>Location Preferred</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isNullorWhiteSpace(data?.campusPreference) === false && data?.campusPreference !== "null" ? data?.campusPreference.split(",").join(", ") : ""}
                    </label>
                  </div>
                ) : (
                  ""
                )}
                {ERPUtils.isUndefinedOrNull(data?.empApplnSubjectCategory) === false ? (
                  <div className="row">
                    {!ERPUtils.isNullorWhiteSpace(data?.academic) && data?.academic === true ? (
                      <label className="col-5">
                        <span>Subject</span>
                      </label>
                    ) : (
                      <label className="col-5">
                        <span>Category</span>
                      </label>
                    )}
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data?.empApplnSubjectCategory) === false ? data?.empApplnSubjectCategory.split(",").join(", ") : ""}
                    </label>
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="mt-2">
        {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO) === false ? (
          <div className="main-details-box">
            <div className="heading-title-style">
              <h5>Personal Details</h5>
            </div>
            <div className="p-2 ml-3">
              <div className="row">
                <div className="col-6">
                  <div className="row">
                    <label className="col-6">
                      <span>Father&apos;s Name</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {!ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.fatherName) ? data.empApplnPersonalDataDTO?.fatherName : "NA"}
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <div className="row">
                    <label className="col-5">
                      <span>Blood Group</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data?.empApplnPersonalDataDTO?.bloodGroupId) === false ? data?.empApplnPersonalDataDTO?.bloodGroupId : "NA"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="row">
                    <label className="col-6">
                      <span>Mother&apos;s Name</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {!ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.motherName) ? data.empApplnPersonalDataDTO?.motherName : "NA"}
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <div className="row">
                    <label className="col-5">
                      <span>Nationality</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.nationalityId) === false ? data.empApplnPersonalDataDTO?.nationalityId : "NA"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="row">
                    <label className="col-6">
                      <span>Age</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {!ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.dateOfBirth)
                        ? calculateAgeHandler(data.empApplnPersonalDataDTO?.dateOfBirth)
                        : "NA"}
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <div className="row">
                    <label className="col-5">
                      <span>Religion</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.religionId) === false ? data.empApplnPersonalDataDTO?.religionId : "NA"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="row">
                    <label className="col-6">
                      <span>Date of Birth</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {!ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.dateOfBirth) ? getDob(data?.empApplnPersonalDataDTO?.dateOfBirth) : "NA"}
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <div className="row">
                    <label className="col-5">
                      <span>Minority</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.isMinority) === false ? data.empApplnPersonalDataDTO?.isMinority : "NA"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="row">
                    <label className="col-6">
                      <span>Gender</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {!ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.genderId) ? data.empApplnPersonalDataDTO?.genderId : "NA"}
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <div className="row">
                    <label className="col-5">
                      <span>Reservation Category</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data?.empApplnPersonalDataDTO?.reservationCategoryId) === false
                        ? data?.empApplnPersonalDataDTO?.reservationCategoryId
                        : "NA"}
                    </label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="row">
                    <label className="col-6">
                      <span>Marital status</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO?.maritalStatusId) === false ? data.empApplnPersonalDataDTO?.maritalStatusId : "NA"}
                    </label>
                  </div>
                </div>

                <div className="col-6">
                  <div className="row">
                    <label className="col-5">
                      <span>Date of Application</span>
                    </label>
                    <span className="col-1">:</span>
                    <label className="col">
                      {ERPUtils.isUndefinedOrNull(data?.submissionDate) === false ? ERPUtils.convertLocalDateToDD_MMM_YYYY_String(data?.submissionDate) : "NA"}
                    </label>
                  </div>
                </div>
              </div>
              {/* <div className="row">
              </div> */}
              <div className="row">
                {!ERPUtils.isNullorWhiteSpace(data?.empApplnPersonalDataDTO?.differentlyAbledId) ? (
                  <div className="col-6">
                    <div className="row">
                      <label className="col-6">
                        <span>Differently Abled</span>
                      </label>
                      <span className="col-1">:</span>
                      <label className="col">
                        {ERPUtils.isUndefinedOrNull(data?.empApplnPersonalDataDTO?.differentlyAbledId) === false
                          ? data?.empApplnPersonalDataDTO?.differentlyAbledId
                          : "NA"}
                      </label>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div>
        {ERPUtils.isUndefinedOrNull(data.empApplnPersonalDataDTO) === false || ERPUtils.isUndefinedOrNull(data.addressDetailDTO) === false ? (
          <div className="main-details-box">
            <div className="heading-title-style">
              <h5>Contact Details</h5>
            </div>
            {contactDetailsTable}
          </div>
        ) : null}
      </div>

      <div>
        {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO) === false ? (
          <div className="main-details-box">
            <div className="heading-title-style">
              <h5>Professional Details</h5>
            </div>
            {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.currentExperience) === false ||
              ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.professionalExperienceList) === false ? (
              <div>{professionalExperience}</div>
            ) : null}
            <div className="avoid-break">{profileSummaryTable}</div>
          </div>
        ) : null}
      </div>
      {/** ********************* EDUCATION DETAILS ******************************* */}
      <div className="avoid-break">
        <div className="main-details-box">
          <div className="heading-title-style">
            <h5>Educational Details</h5>
          </div>
          {educationDetailsTable}
          {ERPUtils.isUndefinedOrNull(data.educationalDetailDTO?.eligibilityTestList) === false ? eligibilityTestTable : null}
        </div>
      </div>
      {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO) === false ? (
        (ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.majorAchievements) === false || !ERPUtils.isNullorWhiteSpace(data.majorAchievements)) ? (
          <div className="avoid-break">
            <div className="main-details-box">
              <div className="heading-title-style">
                <h5>Achievements</h5>
              </div>
              <div className="p-2">
                <label style={{ textAlign: "justify" }} className="normal-text-font-style">
                  <p>
                    {" "}
                    {ERPUtils.isUndefinedOrNull(data.professionalExperienceDTO?.majorAchievements) === false
                      ? data.professionalExperienceDTO?.majorAchievements
                      : data?.majorAchievements}
                  </p>
                </label>
              </div>
            </div>
          </div>
        ) : null
      ) : null}
      <div className="avoid-break ">
        {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO) === false &&
          (!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeApplicationNo) ||
            !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeDepartment) ||
            !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeSubject) ||
            !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeYear) ||
            !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.vacancyInformationId) ||
            !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.otherInformation)
          ) ? (
          <div>
            <div className="main-details-box">
              <div className="heading-title-style">
                <h5>
                  {" "}
                  {!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeApplicationNo) ||
                    !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeDepartment) ||
                    !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeSubject) ||
                    !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.vacancyInformationId) ||
                    !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeYear) ||
                    !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.otherInformation)
                    ? "Additional Details"
                    : ""}
                </h5>
              </div>
              {!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeApplicationNo) ||
                !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeDepartment) ||
                !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeSubject) ||
                !ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeYear) ? (
                <h6 className="previewBox-title p-2 mt-2">
                  <b>Previous Interview Details</b>
                </h6>
              ) : (
                ""
              )}
              <div className="row  ml-3">
                <div className="col-6">
                  {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeApplicationNo) === false ? (
                    <div className="row">
                      <div className="col-6">
                        <span className="sub-head-style">Application No</span>
                      </div>
                      <span className="col-1">:</span>
                      <div className="col">
                        <span className="normal-text-font-style">
                          {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeApplicationNo) === false
                            ? data?.researchDetailDTO?.interviewedBeforeApplicationNo
                            : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeYear) === false ? (
                    <div className="row">
                      <div className="col-6">
                        <span className="sub-head-style">Year</span>
                      </div>
                      <span className="col-1">:</span>
                      <div className="col">
                        <span className="normal-text-font-style">
                          {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeYear) === false
                            ? data?.researchDetailDTO?.interviewedBeforeYear
                            : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeDepartment) === false ? (
                    <div className="row">
                      <div className="col-6">
                        <span className="sub-head-style">Department</span>
                      </div>
                      <span className="col-1">:</span>
                      <div className="col">
                        <span className="normal-text-font-style">
                          {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeDepartment) === false
                            ? data?.researchDetailDTO?.interviewedBeforeDepartment
                            : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <div className="col-6">
                  {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeSubject) === false ? (
                    <div className="row">
                      <div className="col-6">
                        <span className="sub-head-style">Subject</span>
                      </div>
                      <span className="col-1">:</span>
                      <div className="col">
                        <span className="normal-text-font-style">
                          {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.interviewedBeforeSubject) === false
                            ? data?.researchDetailDTO?.interviewedBeforeSubject
                            : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.vacancyInformationId) === false ? (
                    <div className="row">
                      <div className="col-6">
                        <span className="sub-head-style">How did you know about vacancy/position?</span>
                      </div>
                      <span className="col-1">:</span>
                      <div className="col">
                        <span className="normal-text-font-style">
                          {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.vacancyInformationId) === false
                            ? data?.researchDetailDTO?.vacancyInformationId
                            : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div>
                {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.otherInformation) === false ? (
                  <div className="p-2">
                    <label>
                      <div className="sub-head-style">Other Information</div>
                      <div className="normal-text-font-style" style={{ textAlign: "justify" }}>
                        {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.otherInformation) === false ? data?.researchDetailDTO?.otherInformation : ""}
                      </div>
                    </label>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className="avoid-break">
        <div>
          {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO) === false || ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.researchEntries) === false ? (
            ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.inflibnetVidwanNo) === false ||
              ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.scopusId) === false ||
              ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.hIndex) === false ||
              ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.orcidId) === false ||
              ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.researchEntries) === false ? (
              <div className="main-details-box">
                {!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO) === true ? (
                  ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.inflibnetVidwanNo) === false ||
                    ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.scopusId) === false ||
                    ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.hIndex) === false ||
                    ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.orcidId) === false ? (
                    <>
                      <div className="heading-title-style">
                        <h5>Research Details</h5>
                      </div>
                      <div className="p-3 ml-2">
                        <div className="row">
                          {!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.inflibnetVidwanNo) === true ? (
                            <label className="col-6">
                              <div className="row ">
                                <div className="col-6">
                                  <span className="sub-head-style">Vidwan ID </span>
                                </div>
                                <span className="col-1">:</span>
                                <div className="col">
                                  <span className="normal-text-font-style">
                                    {ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.inflibnetVidwanNo) === false
                                      ? data.researchDetailDTO?.inflibnetVidwanNo
                                      : "NA"}
                                  </span>
                                </div>
                              </div>
                            </label>
                          ) : (
                            ""
                          )}
                          {!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.orcidId) === true ? (
                            <label className="col-6">
                              <div className="row">
                                <div className="col">
                                  <span className="sub-head-style">ORCID</span>
                                </div>
                                <span className="col-1">:</span>
                                <div className="col">
                                  <span className="normal-text-font-style">
                                    {ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.orcidId) === false ? (
                                      <span>{data?.researchDetailDTO?.orcidId}</span>
                                    ) : (
                                      "NA"
                                    )}
                                  </span>
                                </div>
                              </div>
                            </label>
                          ) : null}
                          {!ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.scopusId) === true ? (
                            <label className="col-6">
                              <div className="row ">
                                <div className="col-6">
                                  <span className="sub-head-style">Scopus ID </span>
                                </div>
                                <span className="col-1">:</span>
                                <div className="col">
                                  <span className="normal-text-font-style">
                                    {" "}
                                    {ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.scopusId) === false ? data.researchDetailDTO?.scopusId : "NA"}
                                  </span>
                                </div>
                              </div>
                            </label>
                          ) : null}
                          {!ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.scopusId) === true ? (
                            <label className="col-6">
                              <div className="row ">
                                <div className="col-6">
                                  <span className="sub-head-style">Scopus Link </span>
                                </div>
                                <div className="col-6">
                                  {/* : */} {/* <span className="normal-text-font-style"> */} {/* ----------- */}
                                  {/* {ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.scopusId) === false ? data.researchDetailDTO?.scopusId : "NA"} */}
                                  {/* </span> */}
                                </div>
                              </div>
                            </label>
                          ) : null}

                          {/* {!ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.hIndex) === true ? (
                            <label className="col-6 mt-3">
                              <div className="row">
                                <div className="col-6">
                                  <span className="sub-head-style">H Index</span>
                                </div>
                                <div className="col-6">
                                  :
                                  {" "}
                                  <span className="normal-text-font-style">
                                    {ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.hIndex) === false ? data.researchDetailDTO?.hIndex : "NA"}
                                  </span>
                                </div>
                              </div>
                            </label>
                          ) : null} */}
                        </div>
                      </div>
                      {/* </div> */}
                    </>
                  ) : null
                ) : null}
              </div>
            ) : null
          ) : null}
        </div>
      </div>

      {ERPUtils.isUndefinedOrNull(data?.researchDetailDTO) === false || ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.researchEntries) === false ? (
        ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.inflibnetVidwanNo) === false ||
          ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.scopusId) === false ||
          ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.hIndex) === false ||
          ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.orcidId) === false ||
          ERPUtils.isUndefinedOrNull(data?.researchDetailDTO?.researchEntries) === false ? (
          <div>
            {ERPUtils.isUndefinedOrNull(data.researchDetailDTO) === false ? (
              ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.researchEntries) === false ? (
                ERPUtils.isUndefinedOrNull(data.researchDetailDTO?.researchEntries.researchEntriesHeadings) === false ? (
                  <div className="main-details-box">
                    <div className="heading-title-style">
                      <h5>Research summary</h5>
                    </div>
                    <div>
                      {data.researchDetailDTO?.researchEntries?.researchEntriesHeadings.map((el, index) => {
                        const filteredParameters = el.parameters?.filter(
                          (item) => !ERPUtils.isUndefinedOrNull(item.researchParameter) && !ERPUtils.isUndefinedOrNull(item.parameterValue)
                        );
                        // Check if all parameterValue values are either null or 0
                        const allValuesAreNullOrZero = filteredParameters?.every(
                          (item) => item.parameterValue === null || item.parameterValue === "0" || item.parameterValue === 0
                        );
                        // If all parameterValue values are null or 0, do not display the groupHeading
                        if (allValuesAreNullOrZero) {
                          return null;
                        }
                        let counter = 0; // Counter for ordered format
                        // Otherwise, display the groupHeading and parameters
                        return (
                          <div key={index}>
                            <div className="sub-head-style ml-2 mt-2">{el.groupHeading}</div>
                            {filteredParameters?.map((item, idx) => {
                              const parameterValue = item.parameterValue.toString().trim();
                              if (parameterValue !== "" && parameterValue !== "0") {
                                counter++;
                                return (
                                  <div className="row mt-1" key={idx}>
                                    <div className="col-7 normal-text-font-style-ans">
                                      <span className="ml-3">
                                        {counter}. {item.researchParameter}
                                      </span>
                                    </div>
                                    <span className="col-1">:</span>
                                    <div className="col normal-text-font-style-ans">
                                      <span className="">{parameterValue}</span>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null
              ) : null
            ) : null}
          </div>
        ) : null
      ) : null}
      {!ERPUtils.isUndefinedOrNull(data.additionalInformations) ? (
        <div className="main-details-box ">
          <div className="heading-title-style">
            <h5>Additional Information to Application</h5>
          </div>
          {!ERPUtils.isUndefinedOrNull(data?.additionalInformations?.researchEntriesHeadings)
            ? data?.additionalInformations?.researchEntriesHeadings?.map((el, index) => (
              <div key={index}>
                <label>
                  <span className="sub-head-style">{el.groupHeading}</span>
                  {!ERPUtils.isUndefinedOrNull(el?.parameters)
                    ? el.parameters?.map((el1, index1) => (
                      <>
                        <div className="mt-2" key={`${index}_${index1}`}>
                          <label className="ms-3">
                            <span>
                              <b>{`${index1 + 1}. `}</b>
                            </span>{" "}
                            <span className="normal-text-font-style">{el1.researchParameter}</span>
                          </label>
                        </div>
                        <div className="mt-2" key={`${index}_${index1}`}>
                          <label className="ms-4">
                            <span className="normal-text-font-style-ans">{el1.parameterValue}</span>
                          </label>
                        </div>
                      </>
                    ))
                    : null}
                  {!ERPUtils.isUndefinedOrNull(data?.additionalInformations?.researchEntriesHeadings) &&
                    data.additionalInformations.researchEntriesHeadings.length - 1 !== index ? (
                    <br />
                  ) : null}
                </label>
              </div>
            ))
            : null}
        </div>
      ) : null}
    </div>
  );
}
